import { ResponsiveWidth, TableBreakpoint } from './types';

// Coincide con los breakpoints por defecto de Tailwind (sm/md/lg/xl/2xl);
// 3xl/4xl se agregan para pantallas ultra anchas.
const BREAKPOINT_MIN_WIDTH: Partial<Record<TableBreakpoint, number>> = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
  '3xl': 1920,
  '4xl': 2560
};

interface WidthColumn {
  key: string;
  width: ResponsiveWidth;
}

export function getColumnClassName(tableId: string, key: string): string {
  return `tbl-col-${tableId}-${key}`;
}

/**
 * Genera el CSS (con @media reales) que define el ancho de cada columna,
 * mobile-first: un breakpoint sin valor propio hereda el del menor más
 * cercano que sí lo tenga, vía cascada natural de los @media ascendentes.
 */
export function buildColumnWidthCSS(
  tableId: string,
  columns: WidthColumn[],
  actions?: { width: string }
): string {
  const baseRules: string[] = [];
  const rulesByBreakpoint = new Map<number, string[]>();

  const addRule = (key: string, breakpoint: TableBreakpoint | undefined, value: string) => {
    const className = getColumnClassName(tableId, key);
    const declaration = `.${className}{width:${value};}`;
    if (!breakpoint || breakpoint === 'xs') {
      baseRules.push(declaration);
      return;
    }
    const minWidth = BREAKPOINT_MIN_WIDTH[breakpoint];
    if (!minWidth) return;
    const list = rulesByBreakpoint.get(minWidth) ?? [];
    list.push(declaration);
    rulesByBreakpoint.set(minWidth, list);
  };

  columns.forEach(({ key, width }) => {
    if (typeof width === 'string') {
      addRule(key, undefined, width);
      return;
    }
    (Object.keys(width) as TableBreakpoint[]).forEach((breakpoint) => {
      const value = width[breakpoint];
      if (value) addRule(key, breakpoint, value);
    });
  });

  if (actions) addRule('actions', undefined, actions.width);

  const sortedBreakpoints = [...rulesByBreakpoint.keys()].sort((a, b) => a - b);
  const mediaCSS = sortedBreakpoints
    .map(
      (minWidth) =>
        `@media (min-width:${minWidth}px){${rulesByBreakpoint.get(minWidth)!.join('')}}`
    )
    .join('');

  return baseRules.join('') + mediaCSS;
}
